/** Bandeja móvil: fetching con clave tenant-first (páginas sin HTTP directo). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantIdOrNull, tenantScoped } from '@/config/queryClient';
import { tasksService, type WorkflowContext, type WorkflowTask, type WorkflowTaskDetail } from '@/services/tasksService';
import { useMobileAuth } from '@/state/MobileAuthContext';

const DONE_STATUSES: WorkflowTask['status'][] = ['approved', 'rejected'];

export function useMobileTasksQuery(tab: 'open' | 'done') {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowTask[]>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-tasks', tab)
        : ['tenant', 'no-context', 'mobile-tasks', tab],
    queryFn: async () => {
      if (tab === 'open') return tasksService.mine('open');
      const all = await tasksService.mine();
      return all.filter((task) => DONE_STATUSES.includes(task.status));
    },
    enabled: tenantId !== null && hasPermission('workflow_tasks.view_own'),
    staleTime: 30_000,
  });
}

export function useMobileTaskQuery(taskId: string) {
  const { user, hasPermission } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowTaskDetail>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-task', taskId)
        : ['tenant', 'no-context', 'mobile-task', taskId],
    queryFn: () => tasksService.get(taskId),
    enabled: tenantId !== null && taskId !== '' && hasPermission('workflow_tasks.view_own'),
    staleTime: 30_000,
  });
}

export function useMobileTaskContextQuery(entityCode: string | undefined, entityId: string | undefined) {
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useQuery<WorkflowContext>({
    queryKey:
      tenantId !== null
        ? tenantScoped(tenantId, 'mobile-task-context', entityCode, entityId)
        : ['tenant', 'no-context', 'mobile-task-context', entityCode, entityId],
    queryFn: () => tasksService.context(entityCode!, entityId!),
    enabled: tenantId !== null && entityCode !== undefined && entityId !== undefined,
    staleTime: 30_000,
  });
}

export function useDecideTaskMutation() {
  const queryClient = useQueryClient();
  const { user } = useMobileAuth();
  const tenantId = tenantIdOrNull(user?.active_tenant_id);
  return useMutation({
    mutationFn: (input: {
      taskId: string;
      decision: 'approve' | 'reject';
      reason: string | undefined;
      expectedTaskRevision: number;
      comments?: string;
    }) =>
      tasksService.decide(input.taskId, {
        decision: input.decision,
        reason: input.reason,
        comments: input.comments,
        expectedTaskRevision: input.expectedTaskRevision,
      }),
    onSuccess: (_data, variables) => {
      if (tenantId !== null) {
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-tasks') });
        void queryClient.invalidateQueries({
          queryKey: tenantScoped(tenantId, 'mobile-task', variables.taskId),
        });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-task-context') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-document') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-quotations') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-budgets') });
        void queryClient.invalidateQueries({ queryKey: tenantScoped(tenantId, 'mobile-requirements') });
      }
    },
  });
}
